import { useAllSchemeIssues } from "http-hooks/schIssue/useSchemeIssue";
import { generateSchemeIssueReport } from "services/schIssue";
import DeleteSchemeIssue from "components/schIssue/DeleteSchemeIssue";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useModalStore from "store/modals";
import { formatDateAndTime } from "utils/date";
import {
  getDocumentUrls,
  getDocumentViewerRoute,
} from "utils/documentUrlHelper";
import { ActionButton, Badge } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import { EyeIcon } from "@heroicons/react/24/outline";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";

const typeFilterOptions = [
  { value: "", label: "All Types" },
  { value: "B", label: "Bill / Issue" },
  { value: "O", label: "Order" },
];

const initialFilter = {
  batch_id: "",
  schdate: "",
  type: "",
  page: 1,
  limit: 10,
};

const columns = [
  {
    key: "batch_id",
    header: "Batch ID",
    render: (batch) => (
      <span className="font-mono text-xs">{batch.batch_id || "-"}</span>
    ),
  },
  {
    key: "type",
    header: "Type",
    render: (batch) => (
      <Badge variant={batch.type === "O" ? "info" : "success"}>
        {batch.type === "O" ? "Order" : "Issue"}
      </Badge>
    ),
  },
  {
    key: "schdate",
    header: "Date",
    render: (batch) => formatDateAndTime(batch.schdate),
  },
  { key: "schemeName", header: "Scheme" },
  { key: "customerName", header: "Customer" },
  { key: "itemCount", header: "Items" },
  { key: "totalQty", header: "Total Qty" },
  { key: "actions", isActions: true },
];

export default function SchemeIssueMaster() {
  const [viewingReportId, setViewingReportId] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const { openModal } = useModalStore();
  const navigate = useNavigate();

  const openSetModal = (id) => {
    openModal("schissue", id);
  };

  const handleBatchSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, batch_id: searchTerm, page: 1 }));
  };

  const handleDateChange = (e) => {
    setFilter((prev) => ({ ...prev, schdate: e.target.value, page: 1 }));
  };

  const handleTypeChange = (option) => {
    setFilter((prev) => ({ ...prev, type: option?.value || "", page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleViewReport = async (batch) => {
    try {
      setViewingReportId(batch.first_issd_vchno);
      const data = await generateSchemeIssueReport(batch.first_issd_vchno);
      if (data.htmlUrl) {
        const { htmlUrl, pdfUrl } = getDocumentUrls(data);
        const docType = batch.type === "O" ? "Scheme Order" : "Scheme Issue";
        const route = getDocumentViewerRoute(batch.first_issd_vchno, {
          htmlUrl,
          pdfUrl,
          documentNumber: batch.batch_id || "",
          documentType: docType,
        });
        navigate(route);
      }
    } catch (error) {
      console.error("Report generation failed:", error);
    } finally {
      setViewingReportId(null);
    }
  };

  const {
    isLoading: isAllIssuesLoading,
    data: { data: allBatches, page: currentPage, totalPages } = {},
  } = useAllSchemeIssues(filter);

  return (
    <MasterPage
      title="Scheme Issue / Order"
      headerActions={
        <AddButton onClick={() => openSetModal()}>
          Add Scheme Issue / Order
        </AddButton>
      }
      filters={
        <FilterRow>
          <FilterCell>
            <InputSearch
              onSearch={handleBatchSearch}
              label="Batch ID"
              placeholder="Search Batch ID"
            />
          </FilterCell>
          <FilterCell>
            <Input
              type="date"
              label="Date"
              value={filter.schdate}
              onChange={handleDateChange}
            />
          </FilterCell>
          <FilterCell>
            <Select
              label="Type"
              value={typeFilterOptions.find((o) => o.value === filter.type)}
              onChange={handleTypeChange}
              options={typeFilterOptions}
              isClearable={false}
            />
          </FilterCell>
          <ClearFiltersButton onClick={() => setFilter(initialFilter)} />
        </FilterRow>
      }
      columns={columns}
      data={allBatches}
      isLoading={isAllIssuesLoading}
      rowKey="first_issd_vchno"
      renderActions={(batch, setDeleteItemId) => (
        <>
          <ActionButton
            variant="secondary"
            onClick={() => handleViewReport(batch)}
            disabled={viewingReportId === batch.first_issd_vchno}
            title="View/Print"
          >
            {viewingReportId === batch.first_issd_vchno ? (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
            )}
          </ActionButton>
          <EditAction
            onClick={() => openSetModal(batch.first_issd_vchno)}
            variant="primary"
          />
          <DeleteAction
            onClick={() => setDeleteItemId(batch.first_issd_vchno)}
          />
        </>
      )}
      deleteComponent={DeleteSchemeIssue}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
      }}
    />
  );
}
